import { Button, Card, Space } from 'antd'
import React from 'react'
import { useSelector } from 'react-redux'
import { rubricRefinementSelectors } from '../../../../../../store/selectors/rubricRefinement.selectors'
import ChatMessage from './messages/ChatMessage'
import './ChatCard.scss'
import { useMessages } from './messageUtils'
import StartOverButton from './components/StartOverButton'
import FinishRefinementButton from './components/FinishRefinementButton'
import ContinueLaterButton from './components/ContinueLaterButton'

/**
 * @typedef {Object} SystemMessage
 * @property {number} id
 * @property {typeof import('./types').MessageTypes.SYSTEM} type
 * @property {string} content
 * @property {number} [refinementItemId]
 *
 * @typedef {Object} CategoryChoice
 * @property {string} description
 * @property {string} explanation
 *
 * @typedef {Object} CategoryChoiceContent
 * @property {CategoryChoice & { id: string }} categoryA
 * @property {CategoryChoice & { id: string }} categoryB
 *
 * @typedef {Object} CategoryChoiceMessage
 * @property {number} id
 * @property {typeof import('./types').MessageTypes.CATEGORY_CHOICE} type
 * @property {CategoryChoiceContent} content
 * @property {number} [refinementItemId]
 *
 * @typedef {Object} Change
 * @property {number} id
 * @property {string} categoryName
 * @property {string} oldDescription
 * @property {string} newDescription
 * @property {Object} explainability
 *
 * @typedef {Object} DiffReviewContent
 * @property {Change[]} changes
 *
 * @typedef {Object} DiffReviewMessage
 * @property {number} id
 * @property {typeof import('./types').MessageTypes.DIFF_REVIEW} type
 * @property {DiffReviewContent} content
 * @property {number} [refinementItemId]
 *
 * @typedef {SystemMessage | CategoryChoiceMessage | DiffReviewMessage} Message
 */

/**
 * @typedef {Object} RefinementItem
 * @property {number} id
 * @property {Object} originalCategory
 * @property {Object} predictedCategory
 * @property {Object} [suggestedOriginalCategory]
 * @property {Object} [suggestedPredictedCategory]
 * @property {'approved' | 'rejected'} [diffReviewChoice]
 * @property {boolean} [isSkipped]
 */

/**
 * @typedef {'wait' | 'process' | 'finish' | 'error'} StepStatus
 */

const ChatCard = () => {
    const refinementProcess = useSelector(rubricRefinementSelectors.refinementProcess)
    const { messages, isAllItemsReviewed, hasApprovedSuggestions } = useMessages()

    const cardActions = [
        <Button key="progress" type="text" style={{ color: 'var(--ant-color-text-secondary)' }}>
            {refinementProcess?.progress?.itemsReviewed || 0} reviewed
        </Button>,
        <ContinueLaterButton key="continueLater" />,
        <StartOverButton key="startOver" />,
        <FinishRefinementButton 
            key="finish" 
            type={isAllItemsReviewed ? "primary" : "text"}
            hasApprovedSuggestions={hasApprovedSuggestions}
            isAllItemsReviewed={isAllItemsReviewed}
        />
    ]

    return (
        <Card className='chat-card' actions={cardActions}>
            <Space direction='vertical' style={{ width: '100%', marginBottom: 16 }} size={16}>
                {messages.map((message, index) => (
                    <ChatMessage 
                        key={message.id} 
                        message={message}
                        isLastMessage={index === messages.length - 1}
                    />
                ))}
            </Space>
        </Card>
    )
}

export default ChatCard
